import type { ReactNode } from 'react';
import { Hint } from '@/components/Hint';
import { cn } from '@/lib/utils';

/**
 * The acot backend's product page. Every list on the Orders page identifies a
 * product by the same trio — item number, vendor SKU, pid — so that identity
 * text IS the link rather than a separate icon on every row: one target, no
 * extra furniture in tables that are already dense.
 *
 * A row with no pid renders as plain text. Those are real: an invoice line whose
 * product never resolved has nothing to open, and a dead link that looks live is
 * worse than no link.
 */
const BACKEND_PRODUCT_URL = 'https://backend.acherryontop.com/product';

export function ProductLink({ pid, className, children }: {
  pid: number | null | undefined;
  className?: string;
  children: ReactNode;
}) {
  if (pid == null) return <span className={className}>{children}</span>;
  return (
    <Hint content={`Open pid ${pid} in the acot backend`}>
      <a
        href={`${BACKEND_PRODUCT_URL}/${pid}`}
        target="_blank"
        rel="noreferrer"
        // Rows and cards on this page carry their own click handlers (select a
        // receiving, expand a journey) — opening the product must not also trigger
        // whatever the row does.
        onClick={(e) => e.stopPropagation()}
        className={cn('hover:text-primary hover:underline', className)}
      >
        {children}
      </a>
    </Hint>
  );
}

/**
 * `588-64909 · 764-61547 · pid 670495` — the identity line shared by the price
 * check, the discrepancies table and the three-way table, linked to the product.
 */
export function ProductIdentity({ pid, itemnumber, vendorSku, className, linked = true }: {
  pid: number | null | undefined;
  itemnumber: string | null;
  vendorSku?: string | null;
  className?: string;
  /** False prints the identity as plain text (the read-only warehouse page). */
  linked?: boolean;
}) {
  const text = [itemnumber, vendorSku, pid != null ? `pid ${pid}` : null]
    .filter(Boolean)
    .join(' · ');
  if (!text) return null;
  if (!linked) return <span className={className}>{text}</span>;
  return <ProductLink pid={pid} className={className}>{text}</ProductLink>;
}

/**
 * The first cell of every product table: description (clamped) over the
 * linked identity line, with room under it for a row's own notes — a rejected
 * price, a suspect-cost reason. One shape for Move, Discrepancies and Price
 * review, so the item column reads the same in all three.
 */
export function ProductCell({ desc, pid, itemnumber, vendorSku, lines = 2, linked = true, children }: {
  desc: string | null;
  pid: number | null | undefined;
  itemnumber: string | null;
  vendorSku?: string | null;
  /** Description clamp. */
  lines?: 1 | 2;
  linked?: boolean;
  children?: ReactNode;
}) {
  return (
    <>
      <div className={lines === 1 ? 'line-clamp-1' : 'line-clamp-2'}>{desc}</div>
      <ProductIdentity
        pid={pid}
        itemnumber={itemnumber}
        vendorSku={vendorSku}
        linked={linked}
        className="block truncate text-xs text-muted-foreground"
      />
      {children}
    </>
  );
}
